import axios from 'axios';

import { ZENDESK_FORM_IDS } from '@/config/environment';
import { apiV1 } from '@/lib/axios';

interface ZendeskTicket {
  subject: string;
  comment: {
    body: string;
    public: boolean;
    uploads?: string[];
  };
  priority: 'urgent' | 'high' | 'normal' | 'low';
  requester: {
    name: string;
    email: string;
  };
  ticket_form_id: number;
  organization_id?: number;
  email_ccs?: Array<{
    user_email?: string;
    user_id?: string;
    user_name?: string;
    action?: 'put' | 'delete';
  }>;
  custom_fields?: Array<{
    id: number;
    value: string;
  }>;
}

interface CreateTicketRequest {
  ticket: ZendeskTicket;
}

const MIME_TYPES: { [key: string]: string } = {
  pdf: 'application/pdf',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  gif: 'image/gif',
  txt: 'text/plain',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xls: 'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  zip: 'application/zip',
  rar: 'application/x-rar-compressed',
};

const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB

const getContentType = (fileName: string): string => {
  const extension = fileName.toLowerCase().split('.').pop();
  return MIME_TYPES[extension || ''] || 'application/octet-stream';
};

const validateFiles = (files: File[]) => {
  files.forEach((file) => {
    if (file.size > MAX_FILE_SIZE) {
      throw new Error(`O arquivo ${file.name} é muito grande. Tamanho máximo: 50MB`);
    }
  });
};

export const createZendeskTicket = async (data: CreateTicketRequest, files: File[] = []) => {
  const { ticket } = data;
  const { subject, comment, requester, ticket_form_id: ticketFormId } = ticket;

  if (!subject?.trim()) {
    throw new Error('O assunto do ticket é obrigatório');
  }

  if (!comment?.body?.trim()) {
    throw new Error('O corpo da mensagem é obrigatório');
  }

  if (!requester?.name?.trim() || !requester?.email?.trim()) {
    throw new Error('Os dados do solicitante são obrigatórios');
  }

  if (!ticketFormId) {
    throw new Error('O ID do formulário é obrigatório');
  }

  try {
    const hasFiles = files.length > 0;
    let response;

    if (hasFiles) {
      validateFiles(files);
      const formData = new FormData();
      formData.append('ticket', JSON.stringify(ticket));
      files.forEach((file) => {
        formData.append('files', file, file.name);
        formData.append(`contentTypes`, getContentType(file.name));
      });

      response = await apiV1.post(`/v1/support/tickets`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    } else {
      response = await apiV1.post(`/v1/support/tickets`, data, {
        headers: {
          'Content-Type': 'application/json',
        },
      });
    }

    return { success: true, data: response.data };
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const errorMessage = error.response?.data
        ? JSON.stringify(error.response.data)
        : error.message;
      throw new Error(`Erro ao criar ticket no backend: ${errorMessage}`);
    }
    throw error;
  }
};

export const getTicketFormId = (category: string): number => {
  const formId = ZENDESK_FORM_IDS[category as keyof typeof ZENDESK_FORM_IDS];
  if (!formId) {
    throw new Error(`Formulário não encontrado para a categoria: ${category}`);
  }
  return parseInt(formId, 10);
};

const PRIORITY_MAP = {
  alto: 'high',
  medio: 'normal',
  baixo: 'low',
} as const;

const validateServiceTicketParams = (
  organizationName: string,
  service: { item: string },
  message: string,
  requester: { name: string; email: string },
) => {
  if (!organizationName?.trim()) {
    throw new Error('Nome da organização é obrigatório');
  }

  if (!service?.item?.trim()) {
    throw new Error('Item do serviço é obrigatório');
  }

  if (!message?.trim()) {
    throw new Error('Mensagem é obrigatória');
  }

  if (!requester?.name?.trim() || !requester?.email?.trim()) {
    throw new Error('Dados do solicitante são obrigatórios');
  }

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(requester.email)) {
    throw new Error('Email do solicitante inválido');
  }
};

export const createServiceTicket = async (
  organizationName: string,
  service: {
    item: string;
    categoria: string;
    detalhamento: string;
  },
  message: string,
  requester: {
    name: string;
    email: string;
  },
  formData: {
    to: string[];
    emails: string[];
    files: File[];
  },
  organizationId?: number,
  priority: 'alto' | 'medio' | 'baixo' = 'medio',
) => {
  validateServiceTicketParams(organizationName, service, message, requester);

  const ticketSubject = `${organizationName} - ${service.item}`;
  const formId = getTicketFormId(service.categoria);
  const zendeskPriority = PRIORITY_MAP[priority];

  const ticketData: CreateTicketRequest = {
    ticket: {
      subject: ticketSubject,
      comment: {
        body: message,
        public: true,
      },
      priority: zendeskPriority,
      requester,
      ticket_form_id: formId,
      email_ccs: [
        ...formData.emails
          .filter((email) => email.trim() && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()))
          .map((email) => ({
            user_email: email.trim(),
            action: 'put' as const,
          })),
      ],
      ...(organizationId && { organization_id: organizationId }),
    },
  };

  return createZendeskTicket(ticketData, formData.files);
};

export const testZendeskConnection = async (): Promise<boolean> => {
  try {
    const response = await apiV1.get(`/v1/support/health`, {
      headers: {
        'Content-Type': 'application/json',
      },
    });

    return response.status === 200;
  } catch {
    return false;
  }
};
